import styles from "./StartOverlay.module.scss";
import {useEffect, useState} from "react";
import { parseMarkdown } from '@/utils/parseMarkdown';
import Button from "@/components/UI/Button/Button";
import AuthorLink from "@/components/UI/AuthorLink/AuthorLink";
import classNames from "classnames";
import {useCookies} from "react-cookie";
import {useRouter} from "next/router";

type Props = {
  title: string;
  subtitle?: string;
  description?: string;
  author?: string;
  authorSlug?: string;
  logo?: string;
  dateString: string;
  number?: number;
  resume?: boolean;
  onClose?: () => void;
  hideAccountPrompt?: boolean;
  background?: string;
  // Extra content shown under the play button, like a promo banner
  children?: React.ReactNode;
}

const StartOverlay = ({ title, description, logo, dateString, number, resume, onClose, author, authorSlug, subtitle, hideAccountPrompt, background, children }: Props) => {
  const [open, setOpen] = useState(true);
  const [visible, setVisible] = useState(false);
  const [baseLayerVisible, setBaseLayerVisible] = useState(true);
  const router = useRouter()
  const [cookies] = useCookies(['displayName'])
  const displayName = cookies.displayName || null;

  useEffect(() => {
    if (!visible) {
      const timeout = setTimeout(() => {
        setOpen(false);
        onClose && onClose()
      }, 300); // Match with CSS transition duration
      return () => clearTimeout(timeout);
    }
  }, [visible]);

  useEffect(() => {
    setVisible(true);
    const timeout = setTimeout(() => {
      setBaseLayerVisible(false);
    }, 600);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const redirect = router.basePath + router.asPath

  if (!open) return null;

  return (
    <>
    {baseLayerVisible && <div className={classNames(styles.overlay, styles.baseLayer)}/>}
      <div className={styles.overlay} style={!visible ? {opacity: 0} : {}}>
        <div className={styles.overlayInner}>
          {background && <>
            <div className={styles.background} style={{backgroundImage: `url(${background})`}}/>
            <div className={styles.backgroundOverlay}/>
          </>}
          {logo && <img width="100" src={logo} alt={title} className={styles.logo}/>}
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <h3>{subtitle}</h3>}
          {description && <p className={styles.description}>{parseMarkdown(description)}</p>}
          {!displayName && !hideAccountPrompt && (
            <div className={styles.authGroup}>
              Want to save your progress and store stats for your account for free?
              <div className={styles.buttonGroup}>
                <a href={`/signin?utm_source=games&utm_medium=${title}Start&redirect=${redirect}`}><Button color="grey">Sign
                  In</Button></a>
                {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                <a href={`/signup?utm_source=games&utm_medium=${title}Start&redirect=${redirect}`}><Button color="blue">Sign
                  Up</Button></a>
              </div>
            </div>
          )}
          {displayName && (
            <p>Welcome back {displayName}!</p>
          )}
          <b className={styles.date}>
            {dateString}
            {number !== undefined ? ` • #${number}` : ''}
            {author && <> • by <AuthorLink name={author} slug={authorSlug}/></>}
          </b>
          <br/>
          <Button simple onClick={() => setVisible(false)}>{resume ? 'Resume' : 'Play'}</Button>
          {children}
        </div>
      </div>
    </>

  );
}

export default StartOverlay;